import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Tooltip,
  Fab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
  Search as SearchIcon,
  GetApp as ExportIcon,
  Category as CategoryIcon,
  TrendingUp as TrendIcon,
  Assessment as ReportIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { Expense, ExpenseCategory, CreateExpenseInput } from '@hostel-manager/shared';
import ExpenseForm from '../components/expenses/ExpenseForm';
import ExpenseReportingDashboard from '../components/expenses/ExpenseReportingDashboard';
import { fetchWithCacheFallback } from '../services/api-client.service';
import { getApiUrl } from '../config/api';
import { offlineCacheService } from '../services/offline-cache.service';
import { CACHE_KEYS } from '../config/cache-config';
import { useMobileShell } from '../hooks/useMobileShell';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && (
        <Box sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>{children}</Box>
      )}
    </div>
  );
}

interface ExpenseFilters {
  category: ExpenseCategory | 'all';
  paymentMethod: 'Online' | 'Cash' | 'all';
  startDate: string;
  endDate: string;
  search: string;
}

const Expenses: React.FC = () => {
  const isMobile = useMobileShell();
  const [tabValue, setTabValue] = useState(0);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Dialog states
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  
  // Filter state
  const [filters, setFilters] = useState<ExpenseFilters>({
    category: 'all',
    paymentMethod: 'all',
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    search: '',
  });

  // Available categories and tags
  const expenseCategories: ExpenseCategory[] = [
    'electricity',
    'water',
    'salary',
    'maintenance',
    'supplies',
    'cleaning',
    'repairs',
    'equipment',
    'refund',
    'advertising',
    'security',
    'other',
  ];

  const loadExpenses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (filters.category !== 'all') params.set('category', filters.category);
      if (filters.paymentMethod !== 'all') params.set('paymentMethod', filters.paymentMethod);
      if (filters.startDate) params.set('startDate', filters.startDate);
      if (filters.endDate) params.set('endDate', filters.endDate);
      if (filters.search) params.set('search', filters.search);
      const { data } = await fetchWithCacheFallback<{ success: boolean; data: Expense[]; pagination?: { total: number } }>(
        `/api/expenses?${params}`
      );
      const list = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
      let filteredExpenses = list.map((e: Partial<Expense> & { date?: string | Date }) => ({
        ...e,
        date: e.date ? new Date(e.date) : new Date(),
      })) as Expense[];

      if (filters.category !== 'all') {
        filteredExpenses = filteredExpenses.filter(expense => expense.category === filters.category);
      }

      if (filters.paymentMethod !== 'all') {
        filteredExpenses = filteredExpenses.filter(expense => expense.paymentMethod === filters.paymentMethod);
      }

      if (filters.startDate && filters.endDate) {
        const start = new Date(filters.startDate);
        const end = new Date(filters.endDate);
        filteredExpenses = filteredExpenses.filter(expense => 
          expense.date >= start && expense.date <= end
        );
      }

      if (filters.search) {
        const query = filters.search.toLowerCase();
        filteredExpenses = filteredExpenses.filter(expense =>
          expense.description.toLowerCase().includes(query) ||
          expense.category.toLowerCase().includes(query) ||
          expense.receiptReference?.toLowerCase().includes(query) ||
          expense.tags?.some(tag => tag.toLowerCase().includes(query))
        );
      }

      setExpenses(filteredExpenses);
    } catch (err) {
      setError('Failed to load expenses');
      setExpenses([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadExpenses();
  }, [loadExpenses]);

  const handleSaveExpense = async (data: CreateExpenseInput) => {
    try {
      setLoading(true);
      const baseHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
      const token = localStorage.getItem('auth_token');
      if (token) baseHeaders['Authorization'] = `Bearer ${token}`;
      const payload = {
        amount: data.amount,
        date: data.date instanceof Date ? data.date.toISOString().split('T')[0] : data.date,
        category: data.category,
        description: data.description,
        paymentMethod: data.paymentMethod,
        receiptReference: data.receiptReference || undefined,
        monthAllocation: data.monthAllocation,
        approvedBy: data.approvedBy || undefined,
      };

      if (editingExpense) {
        const res = await fetch(getApiUrl(`/api/expenses/${editingExpense.id}`), {
          method: 'PUT',
          headers: baseHeaders,
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      } else {
        const res = await fetch(getApiUrl('/api/expenses'), {
          method: 'POST',
          headers: baseHeaders,
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      }

      offlineCacheService.remove(CACHE_KEYS.EXPENSES);
      setExpenseDialogOpen(false);
      setEditingExpense(null);
      await loadExpenses();
    } catch (err) {
      setError('Failed to save expense');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleEditExpense = (expense: Expense) => {
    setEditingExpense(expense);
    setExpenseDialogOpen(true);
  };

  const handleDeleteExpense = async (id: string) => {
    try {
      setLoading(true);
      const headers: Record<string, string> = {};
      const token = localStorage.getItem('auth_token');
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(getApiUrl(`/api/expenses/${id}`), {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      offlineCacheService.remove(CACHE_KEYS.EXPENSES);
      await loadExpenses();
    } catch (err) {
      setError('Failed to delete expense');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingExpense(null);
  };

  const getCategoryColor = (category: ExpenseCategory) => {
    const colors: Record<ExpenseCategory, 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'> = {
      salary: 'primary',
      electricity: 'warning',
      water: 'info',
      maintenance: 'error',
      supplies: 'success',
      cleaning: 'success',
      repairs: 'error',
      equipment: 'secondary',
      refund: 'primary',
      advertising: 'secondary',
      security: 'warning',
      other: 'primary' as const,
    };
    return colors[category] || 'primary';
  };

  const getTotalExpenses = () => {
    return expenses.reduce((sum, expense) => sum + expense.amount, 0);
  };

  const handleExportExpenses = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        startDate: filters.startDate,
        endDate: filters.endDate,
      });
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/api/reports/export/expenses/csv?${params}`), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `expenses-${filters.endDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export expenses');
    } finally {
      setLoading(false);
    }
  };

  const getExpenseStats = () => {
    const total = getTotalExpenses();
    const count = expenses.length;
    const average = count > 0 ? total / count : 0;

    // Category breakdown
    const categoryTotals: Record<string, number> = {};
    expenses.forEach(expense => {
      categoryTotals[expense.category] = (categoryTotals[expense.category] || 0) + expense.amount;
    });

    const topCategory = Object.entries(categoryTotals).sort(([,a], [,b]) => b - a)[0];

    return {
      total,
      count,
      average,
      topCategory: topCategory ? { category: topCategory[0], amount: topCategory[1] } : null,
    };
  };

  const ExpensesCards = () => (
    <Box display="flex" flexDirection="column" gap={2}>
      {expenses.map((expense) => (
        <Card key={expense.id} variant="outlined">
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1} flexWrap="wrap" gap={1}>
              <Chip label={expense.category} color={getCategoryColor(expense.category)} size="small" />
              <Typography variant="subtitle1" fontWeight={600}>₹{expense.amount.toLocaleString()}</Typography>
            </Box>
            <Typography variant="body2" fontWeight={600}>{expense.description}</Typography>
            <Typography variant="body2" color="text.secondary">
              {expense.date.toLocaleDateString()} · {expense.paymentMethod}
              {expense.receiptReference && ` · ${expense.receiptReference}`}
            </Typography>
            {expense.tags && expense.tags.length > 0 && (
              <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {expense.tags.slice(0, 3).map(tag => (
                  <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                ))}
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5 }}>
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => handleEditExpense(expense)} sx={{ minWidth: 44, minHeight: 44 }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton size="small" color="error" onClick={() => handleDeleteExpense(expense.id)} sx={{ minWidth: 44, minHeight: 44 }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  const ExpensesTable = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Category</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Amount</TableCell>
            <TableCell>Payment</TableCell>
            <TableCell>Receipt</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {expenses.map((expense) => (
            <TableRow key={expense.id}>
              <TableCell>
                {expense.date.toLocaleDateString()}
              </TableCell>
              <TableCell>
                <Chip
                  label={expense.category}
                  color={getCategoryColor(expense.category)}
                  size="small"
                />
              </TableCell>
              <TableCell>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {expense.description}
                  </Typography>
                  {expense.tags && expense.tags.length > 0 && (
                    <Box sx={{ mt: 0.5 }}>
                      {expense.tags.slice(0, 3).map(tag => (
                        <Chip 
                          key={tag} 
                          label={tag} 
                          size="small" 
                          variant="outlined" 
                          sx={{ mr: 0.5, fontSize: '0.7rem' }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight={600}>
                  ₹{expense.amount.toLocaleString()}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={expense.paymentMethod}
                  color={expense.paymentMethod === 'Online' ? 'success' : 'default'}
                  size="small"
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {expense.receiptReference ? (
                    <Tooltip title={expense.receiptReference}>
                      <ReceiptIcon color="success" fontSize="small" />
                    </Tooltip>
                  ) : (
                    <Tooltip title="No receipt">
                      <ReceiptIcon color="disabled" fontSize="small" />
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Edit Expense">
                    <IconButton 
                      size="small" 
                      onClick={() => handleEditExpense(expense)}
                      sx={{ minWidth: 44, minHeight: 44 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Expense">
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => handleDeleteExpense(expense.id)}
                      sx={{ minWidth: 44, minHeight: 44 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const ExpenseStatsCards = () => {
    const stats = getExpenseStats();
    
    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <MoneyIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Total Expenses</Typography>
              </Box>
              <Typography variant="h4" color="primary.main">
                ₹{stats.total.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendIcon color="secondary" sx={{ mr: 1 }} />
                <Typography variant="h6">Transactions</Typography>
              </Box>
              <Typography variant="h4" color="secondary.main">
                {stats.count}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ReportIcon color="info" sx={{ mr: 1 }} />
                <Typography variant="h6">Average</Typography>
              </Box>
              <Typography variant="h4" color="info.main">
                ₹{stats.average.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CategoryIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="h6">Top Category</Typography>
              </Box>
              <Typography variant="h4" color="success.main">
                {stats.topCategory ? stats.topCategory.category : 'N/A'}
              </Typography>
              {stats.topCategory && (
                <Typography variant="caption" color="textSecondary">
                  ₹{stats.topCategory.amount.toLocaleString()}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 2, sm: 0 },
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
            Expense Management
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Track and categorize all hostel operating expenses.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            resetForm();
            setExpenseDialogOpen(true);
          }}
          sx={{ minHeight: 44, alignSelf: { xs: 'stretch', sm: 'auto' } }}
        >
          Add Expense
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-indicator': { height: 3 },
            '& .Mui-selected': { fontWeight: 600 },
          }}
        >
          <Tab label="Expenses List" />
          <Tab label="Category Analysis" />
          <Tab label="Reports" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <ExpenseStatsCards />

          {/* Filters */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              mb: 3,
              flexWrap: 'wrap',
              alignItems: { xs: 'stretch', sm: 'center' },
            }}
          >
            <TextField
              placeholder="Search expenses..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
              }}
              sx={{ minWidth: { xs: 0, sm: 250 } }}
            />
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={filters.category}
                label="Category"
                onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value as ExpenseCategory | 'all' }))}
              >
                <MenuItem value="all">All Categories</MenuItem>
                {expenseCategories.map(category => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={filters.paymentMethod}
                label="Payment Method"
                onChange={(e) => setFilters(prev => ({ ...prev, paymentMethod: e.target.value as 'all' | 'Cash' | 'Online' }))}
              >
                <MenuItem value="all">All Methods</MenuItem>
                <MenuItem value="Cash">Cash</MenuItem>
                <MenuItem value="Online">Online</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Start Date"
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: { xs: '100%', sm: 160 } }}
            />
            <TextField
              label="End Date"
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: { xs: '100%', sm: 160 } }}
            />
            <Button
              variant="outlined"
              startIcon={<ExportIcon />}
              onClick={handleExportExpenses}
              sx={{ minHeight: 44 }}
            >
              Export
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : isMobile ? (
            <ExpensesCards />
          ) : (
            <ExpensesTable />
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            Category Analysis
          </Typography>
          <Typography color="textSecondary">
            This section will show category-wise expense breakdown and trends.
          </Typography>
          {/* Category analysis charts will be implemented in T081 */}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <ExpenseReportingDashboard />
        </TabPanel>
      </Card>

      {/* Add/Edit Expense Dialog */}
      <Dialog open={expenseDialogOpen} onClose={() => { setExpenseDialogOpen(false); setEditingExpense(null); }} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingExpense ? 'Edit Expense' : 'Add New Expense'}
        </DialogTitle>
        <DialogContent>
          <ExpenseForm
            expense={editingExpense}
            onSubmit={handleSaveExpense}
            onCancel={() => { setExpenseDialogOpen(false); setEditingExpense(null); }}
          />
        </DialogContent>
      </Dialog>

      {/* Floating Action Button for mobile */}
      <Fab
        color="primary"
        aria-label="add expense"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          display: { xs: 'flex', sm: 'none' },
        }}
        onClick={() => {
          resetForm();
          setExpenseDialogOpen(true);
        }}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default Expenses;